import { z } from "zod";

/**
 * Каталог доменных событий. Публикуются в той же транзакции, что и изменение (через pg-boss),
 * обработчики регистрируются в воркере. Имя + версия + схема данных. Этап добавляет событие отдельным PR.
 */
const base = { salonId: z.string(), at: z.coerce.date() };

export const EventSchemas = {
  "message.received": z.object({ ...base, conversationId: z.string(), messageId: z.string(), author: z.enum(["client", "staff"]) }),
  "message.sent": z.object({ ...base, conversationId: z.string(), messageId: z.string(), author: z.enum(["bot", "staff", "system"]) }),
  "conversation.escalated": z.object({ ...base, conversationId: z.string(), reason: z.string(), note: z.string().optional() }),
  "conversation.taken_over": z.object({ ...base, conversationId: z.string(), userId: z.string() }),
  "conversation.bot_resumed": z.object({ ...base, conversationId: z.string() }),
  "hold.placed": z.object({ ...base, bookingId: z.string(), conversationId: z.string().nullable() }),
  "hold.expired": z.object({ ...base, bookingId: z.string() }),
  "booking.created": z.object({ ...base, bookingId: z.string(), status: z.enum(["pending", "confirmed"]), source: z.enum(["bot", "master", "booking_page"]) }),
  "booking.confirmed": z.object({ ...base, bookingId: z.string(), by: z.enum(["staff", "auto"]) }),
  "booking.rejected": z.object({ ...base, bookingId: z.string(), reason: z.string().optional() }),
  "booking.cancelled": z.object({ ...base, bookingId: z.string(), by: z.enum(["client", "staff"]), late: z.boolean() }),
  "booking.rescheduled": z.object({
    ...base,
    bookingId: z.string(),
    previousStart: z.coerce.date(),
    by: z.enum(["client", "staff"]).optional(),
    late: z.boolean().optional(),
  }),
  "booking.no_show": z.object({ ...base, bookingId: z.string(), clientId: z.string().nullable() }),
  "client.running_late": z.object({ ...base, bookingId: z.string(), minutes: z.number().int() }),
  "master.time_off_set": z.object({ ...base, masterId: z.string(), affectedBookingIds: z.array(z.string()) }),
  "question.unanswered": z.object({ ...base, conversationId: z.string(), question: z.string() }),
  "bot.failed": z.object({ ...base, conversationId: z.string(), error: z.string() }),
  "channel.disconnected": z.object({ ...base, channel: z.string(), reason: z.string() }),
  "channel.token_expiring": z.object({ ...base, channel: z.string(), expiresAt: z.coerce.date() }),
  "usage.cap_reached": z.object({ ...base, costUsd: z.number() }),
  "subscription.changed": z.object({ ...base, status: z.string() }),
} as const;

export type EventName = keyof typeof EventSchemas;
export type EventPayload<N extends EventName> = z.infer<(typeof EventSchemas)[N]>;
export type DomainEvent = { [N in EventName]: { name: N; version: 1; payload: EventPayload<N> } }[EventName];

export const EventNames = Object.keys(EventSchemas) as EventName[];

export const parseEvent = <N extends EventName>(name: N, payload: unknown): EventPayload<N> => EventSchemas[name].parse(payload) as EventPayload<N>;
